import { useAuth0 } from '@auth0/auth0-react';
import { ErrorBanner } from '@my/ui';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { UploadStep } from './steps/UploadStep';
import { MappingStep } from './steps/MappingStep';
import { PreviewStep } from './steps/PreviewStep';
import { CommitStep } from './steps/CommitStep';
import { autoMatchColumns } from './lib/columnMatch';
import { buildDrafts } from './lib/buildDrafts';
import {
  DEFAULT_IMPORT_DEFAULTS,
  type ColumnMapping,
  type ImportDefaults,
  type ImportDraft,
  type ParsedFile,
  type WizardStep,
} from './lib/types';

const STEPS: { id: WizardStep; label: string }[] = [
  { id: 'upload', label: 'Upload' },
  { id: 'map', label: 'Map columns' },
  { id: 'preview', label: 'Preview & fix' },
  { id: 'commit', label: 'Import' },
];

/**
 * Contacts tab of Settings → Import. A 4-step wizard that turns a JSON / CSV /
 * Excel onboarding file into customers or suppliers:
 *   upload → map columns → preview & fix (with geocode) → commit per-row.
 *
 * v1 reuses the existing create-address + create-party endpoints, driven
 * per-row on the FE with per-row try/catch so a failed line stays editable
 * and retryable. Owner / Admin only (gated at the route).
 *
 * Renders as a tab section — the page header + tab strip live in the parent
 * `ImportPage`, so this component owns only the stepper and step bodies.
 */
export function ContactsImportTab() {
  const { user } = useAuth0();
  const navigate = useNavigate();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const [step, setStep] = useState<WizardStep>('upload');
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [defaults, setDefaults] = useState<ImportDefaults>(DEFAULT_IMPORT_DEFAULTS);
  const [drafts, setDrafts] = useState<ImportDraft[]>([]);
  const [excludedRefs, setExcludedRefs] = useState<Set<string>>(() => new Set());

  const stepIndex = STEPS.findIndex((s) => s.id === step);

  const patchDraft = (rowRef: string, patch: Partial<ImportDraft>) =>
    setDrafts((prev) => prev.map((d) => (d.rowRef === rowRef ? { ...d, ...patch } : d)));

  const toggleExclude = (rowRef: string) =>
    setExcludedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(rowRef)) next.delete(rowRef);
      else next.add(rowRef);
      return next;
    });

  const setAllExcluded = (excluded: boolean) =>
    setExcludedRefs(excluded ? new Set(drafts.map((d) => d.rowRef)) : new Set());

  const handleParsed = (next: ParsedFile, sourceFile: File) => {
    setParsed(next);
    setFile(sourceFile);
    // Seed the column mapping from the new file's headers (auto-match). Re-seed
    // whenever a fresh file / sheet is parsed so the suggestions track the data.
    setMapping(autoMatchColumns(next.headers));
  };

  // Materialise drafts when leaving the mapping step, then advance to preview.
  const handleMappingContinue = () => {
    if (!parsed) return;
    setDrafts(buildDrafts(parsed, mapping, defaults));
    setExcludedRefs(new Set()); // fresh build → nothing excluded yet
    setStep('preview');
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto pb-4">
      {/* Stepper */}
      <ol className="flex items-center gap-2 text-xs">
        {STEPS.map((s, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          return (
            <li key={s.id} className="flex items-center gap-2">
              <span
                className={
                  'flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ' +
                  (active
                    ? 'bg-purple-600 text-white'
                    : done
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-500')
                }
              >
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={active ? 'font-medium text-slate-900' : 'text-slate-500'}>
                {s.label}
              </span>
              {i < STEPS.length - 1 && <span className="mx-1 text-slate-300">→</span>}
            </li>
          );
        })}
      </ol>

      <section className="rounded-md border border-slate-200 bg-white p-4">
        {step === 'upload' && (
          <UploadStep
            parsed={parsed}
            file={file}
            onParsed={handleParsed}
            onContinue={() => setStep('map')}
          />
        )}

        {step === 'map' && parsed && (
          <MappingStep
            parsed={parsed}
            mapping={mapping}
            defaults={defaults}
            onMappingChange={setMapping}
            onDefaultsChange={setDefaults}
            onBack={() => setStep('upload')}
            onContinue={handleMappingContinue}
          />
        )}

        {step === 'preview' && (
          <PreviewStep
            drafts={drafts}
            excludedRefs={excludedRefs}
            onDraftChange={patchDraft}
            onToggleExclude={toggleExclude}
            onSetAllExcluded={setAllExcluded}
            onBack={() => setStep('map')}
            onContinue={() => setStep('commit')}
          />
        )}

        {step === 'commit' &&
          (orgId ? (
            <CommitStep
              drafts={drafts}
              excludedRefs={excludedRefs}
              organisationId={orgId}
              onDraftChange={patchDraft}
              onBack={() => setStep('preview')}
              onDone={() => navigate(defaults.type === 'Supplier' ? '/suppliers' : '/customers')}
            />
          ) : (
            <ErrorBanner
              title="No organisation"
              message="Couldn't resolve the current organisation — sign in again or pick an organisation, then retry the import."
            />
          ))}
      </section>
    </main>
  );
}
